import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppButton } from '@/shared/components/buttons/AppButton';
import { BACKDOOR_TOKEN_KEY } from '@/shared/constants/auth';
import { buildParentAppContinuationUrl } from '../utils/shareOffer';

export default function ShareOfferHandoffPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const offerId = searchParams.get('offerId') || '';
  const targetPath = offerId ? `/offers/${offerId}` : '/';
  const hasToken =
    Boolean(sessionStorage.getItem(BACKDOOR_TOKEN_KEY)) ||
    Boolean(localStorage.getItem(BACKDOOR_TOKEN_KEY));

  const parentAppUrl = useMemo(() => {
    const parentHandoffBase = import.meta.env.VITE_PARENT_APP_HANDOFF_URL;

    if (!parentHandoffBase) {
      return null;
    }

    try {
      return buildParentAppContinuationUrl(parentHandoffBase, targetPath);
    } catch {
      return null;
    }
  }, [targetPath]);

  const handleRetry = () => {
    if (!offerId) {
      navigate('/', { replace: true });
      return;
    }

    if (hasToken) {
      navigate(targetPath, { replace: true });
      return;
    }

    globalThis.location.reload();
  };

  const handleOpenParentApp = () => {
    if (!parentAppUrl) {
      return;
    }

    globalThis.location.href = parentAppUrl;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper sx={{ p: 3, maxWidth: 460, width: '100%' }}>
        <Stack spacing={2.5}>
          <Typography variant="h6">{t('offerDetail.shareHandoff.title')}</Typography>
          {!offerId ? (
            <Typography color="error">{t('offerDetail.shareHandoff.invalidLink')}</Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('offerDetail.shareHandoff.description')}
              </Typography>
              {!parentAppUrl && (
                <Typography variant="body2" color="warning.main">
                  {t('offerDetail.shareHandoff.parentAppUnavailable')}
                </Typography>
              )}
            </>
          )}

          <Stack direction="column" spacing={1.5}>
            <AppButton
              variant="primary"
              onClick={handleOpenParentApp}
              disabled={!offerId || !parentAppUrl}
              data-testid="offer-share-handoff-open-parent-app"
            >
              {t('offerDetail.shareHandoff.openParentApp')}
            </AppButton>
            <AppButton
              variant="outlined"
              onClick={handleRetry}
              data-testid="offer-share-handoff-retry"
            >
              {t('offerDetail.shareHandoff.retry')}
            </AppButton>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
